import type { Language } from "@local/i18n/src/languages"
import type { Listing, MediaInfo } from "../common/types"

export enum ShippingMethod {
  STANDARD = "STANDARD",
  SUPER_EXPRESS = "SUPER_EXPRESS",
}

export namespace Cart {
  // Base cart item interface
  export interface BaseItem {
    attributes?: Record<string, string>
    // DEV-9544 (i18n): display-only localized sibling of `attributes`.
    // attrName -> localized display string (e.g. { color: "Marineblau" }),
    // resolved from the SKU's `attributeDisplayValues` at add-to-cart time.
    // Absent on English markets / unlocalized attrs; the cart renderer falls
    // back to the raw `attributes` value. `attributes` stays the raw EN match
    // key (SKU match/commit still use it) — this never affects matching.
    attributeDisplayValues?: Record<string, string>
    // DEV-9947 (i18n): which language `attributeDisplayValues` was resolved in.
    // The cart is one bucket per MARKET and is shared across that market's
    // language variants (DEV-9850), so an item added on /de keeps its German
    // labels after the shopper switches to /de-en. This stamp is what makes the
    // staleness detectable. ABSENT MEANS LEGACY, NOT ENGLISH: items added before
    // this shipped carry no stamp and are left alone rather than guessed at.
    attributeDisplayLanguage?: Language
    createdAt: string
    discountPrice: number
    discount?: {
      amount: number
      type: "percentage" | "fixed"
      source: string
    }
    id: string
    index: number
    legacyVariantId: string | null // Legacy Variant ID from Offer service
    legacyId: string // Legacy ID from Offer service
    listing?: Listing
    listingId: string // Listing ID from Offer service
    media: MediaInfo
    name: string
    parentItemId?: string
    preOrder: Date | null
    isGiftCard?: boolean
    stock: number | null
    price: number
    priceTiers: Array<{
      value: number
      min: number
      max: number | null
    }>
    productName: string
    promotionalType?: string
    sku?: string // Individual SKU identifier
    skuListId?: string // Parent SKUList ID from PIN
    slug?: string
    taxonomyId: number | null // Google Taxonomy ID
    taxonomyName: string | null // Google Taxonomy Name
  }

  // Single cart item
  export interface SingleItem extends BaseItem {
    type: "single"
    bundleItems?: never
  }

  // Bundle cart item
  export interface BundleItem extends BaseItem {
    type: "bundle"
    bundleItems: SingleItem[]
    bundleVariantId?: string
  }

  // Union type for all cart items
  export type Item = SingleItem | BundleItem

  // Cart totals
  export interface Totals {
    subtotal: number
    tax: number
    shipping: number
    discount: number
    giftCard: number
    tip: number
    grandTotal: number
  }

  // Cart discount
  export interface Discount {
    code: string
    amount: number
    type: "percentage" | "fixed"
    minSpend: number
    source: string
  }

  // Gift card
  export interface GiftCard {
    code: string
    amount: number
    currency?: string
  }

  // Tax information
  export interface TaxInfo {
    amount: number
    status: "estimated" | "final"
  }

  // Base cart state data properties
  type BaseStateData = {
    channel: string
    convertedCart?: string | null
    currency: string
    customShippingCost?: number
    id: string
    items: Item[]
    lastUpdated: string
    progressiveDiscountPercentage?: number
    region: string
    shippingMethod?: ShippingMethod
    tax: TaxInfo
    tip: number
    totals: Totals
    uid: string
  }

  // Complete cart state data with discriminated union for discount/giftCard
  // Ensures discount and giftCard cannot coexist
  export type StateData = BaseStateData &
    (
      | { discount: Discount; giftCard?: never }
      | { discount?: never; giftCard: GiftCard }
      | { discount?: never; giftCard?: never }
    )

  export interface CartEvent extends Event {
    detail: {
      channel: string
      region: string
      state: StateData
    }
  }
}
